// Package imports:
import React, { MutableRefObject, ReactNode, forwardRef, useMemo, useRef } from 'react';
import cx from 'classnames';
import Bugsnag from '@bugsnag/js';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight,
    // faFileMagnifyingGlass,
    faFilePdf, faXmark } from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import Button from '../../ui-elements/Button/Button';
import Input from '../../ui-elements/Input/Formik/Input';
import Link from '../../ui-elements/Link/Link';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import HistoryButtonDropDown from '../HistoryButtonDropDown/HistoryButtonDropDown';
import Table from '../../ui-elements/Table/Table';
import Loading from '../../ui-elements/Loading/Loading';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import DisplayBox from '../../components/DisplayBox/DisplayBox';
// Service imports:
import { insertDash, isSSN } from '../../services/utils';
import { useBuyingProcess } from '../../services/buyhook';
import { GET_KELDAN_API_URL } from '../../services/config';
// Type imports:
import { IKeldanApiResponse } from '../../types/KeldanTypes';
import { IPepType, PepSubscription, Result } from '../../types/PepTypes';

interface IProps {
    isAuthenticated: boolean,
    subscriptions: PepSubscription[] | null
}

export interface IPepData {
    connected: boolean,
    name: string | undefined,
    ssn: string | undefined,
    entries: IPepArray[],
}
interface IPepArray {
    name: string,
    connection: string,
    status: string,
    source: string | JSX.Element | null,
}

const relatesToFormer = (date: string | number | null) => {
    return date !== null && new Date(date) < new Date();
}

export const sortEntries = (results: Result[] | null) => {
    if (!results || results.length === 0) {
        return [];
    }

    const arrayToReturn: IPepArray[] = [];
    for (const result of results) {
        const { name, ssn, entries } = result; // Extract name, ssn, and entries from the current Result object
        if(entries === null) return []

        const primaryData = entries.filter(x => x.primaryEntry);
        const secondaryData = entries.filter(x => !x.primaryEntry);
        for (var x of primaryData) {
            arrayToReturn.push({
                name: `${name} (${insertDash(ssn)})`,
                connection: 'Frumaðili',
                status: `${x.position} (${x.category})`,
                source: relatesToFormer(x.endDate) ? x.notes : <Link url={x.source ?? '#'} linkSize='14' target='_blank'>{x.source}</Link>,
            });
        }
        for (var y of secondaryData) {
            arrayToReturn.push({
                name: `${y.relatesToName} (${insertDash(y.relatesToSsn)})`,
                connection: `${y.category}`,
                status: `${y.relatesToPosition} (${y.relatesToCategory})`,
                source: relatesToFormer(y.relatesToEndDate) ? y.relatesToNotes : <Link url={y.relatesToSource} linkSize='14' target='_blank'>{y.relatesToSource}</Link>,
            });
        }
    }
    return arrayToReturn;
};

const PEPSearch: React.FC<IProps> = ({ isAuthenticated, subscriptions }) => {
    const [data, setData] = React.useState<(IPepData & {event: {
        id: number,
        date: string,
    }}) | null>()
    const cleanResultsAndForm =  () => {
        setData(undefined);
    }
    const searchResultsRef = useRef<HTMLDivElement>(null);

    const getPurchaseOption: 'Leita' | 'Kaupa'  = useMemo(() => {
        //will also be disabled
        if(subscriptions === null) return 'Leita';
        for (const subscription of subscriptions) {
          if (subscription.Active && subscription.ProductId === 20) {
            const instance = subscription.ProductInstances?.find(inst => inst.ProductId === 20);
            if (instance) {
              return instance.Discount === 1 ? "Leita" : "Kaupa";
            }
          }
        }
        return "Leita"; // Default if not found
      } ,[subscriptions])

    const { tryPurchaseItem, isItemLoading, setToastError, resetState, ModalsAndToasts } = useBuyingProcess(async (ssn: string, modalReference: string) => {
        try {
            const requestQuery = new URLSearchParams({
                id: ssn, 
                reference: modalReference
            })
            //indicate loading
            setData(null)
            const url = `${GET_KELDAN_API_URL()}/PEP/BuyReport?${requestQuery.toString()}`
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                }
            });
            if (response.ok) {
                const responseBody: IKeldanApiResponse<IPepType & {event: {
                    id: number,
                    date: string,
                }}> = await response.json();
                if (responseBody.success && responseBody.result && responseBody.result.results[0]) {
                    // Sort the entries and set the data
                    setData({
                        name: responseBody.result.results[0].name,
                        ssn: responseBody.result.results[0].ssn,
                        connected: responseBody.result.results[0].connected,
                        entries: sortEntries(responseBody.result.results ?? null),
                        event: {
                            id: responseBody.result.report.balls,
                            date: responseBody.result.report.date
                        }
                    });
                    searchResultsRef.current?.scrollIntoView();
                }
                else {
                    setToastError("bad request");
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error');
        }
    },  {
        customHeader: 'PEP listi - stjórnmálaleg tengsl',
        customText: ' '
        })
    const displayForm = (disabled: boolean) => (
        <Formik
            initialValues={{
                ssn: ''
            }}
            validationSchema={Yup.object({
                ssn: Yup.string().test('ssn-test', 'Vinsamlegast sláðu inn gilda kennitölu', (value) => isSSN(value)).required('Leit má ekki vera tóm')
            })}
            onSubmit={(values) => {
                const trimmed = values.ssn.trim().replaceAll('-', '');
                if(isSSN(trimmed)){
                    getPurchaseOption === "Kaupa"
                    ? tryPurchaseItem(trimmed, 'confirm-modal-with-reference')
                    : tryPurchaseItem(trimmed, 'confirm-modal-with-reference')
                }
            }}
            component={(props: { values: ISubFormValues }) => (
                <FormSubComponent
                    disabled={disabled}
                    values={props.values}
                    loading={isItemLoading(props.values.ssn)}
                    buttonString={getPurchaseOption}
                />
            )}
        />
    )
    const isDisabled = useMemo(() => {
        return !(subscriptions !== null && isAuthenticated && subscriptions.some(sub => sub.ProductId === 20));
    },[isAuthenticated, subscriptions])

    const displayResultSection = () => {
        // null indicates loading
        if (data === null) return <SectionWrapper ref={searchResultsRef}><Loading /></SectionWrapper>;
        if(data === undefined) return <></>
        // Check if the first entry is connected
            
        return (
            <SectionWrapper ref={searchResultsRef}>
                <div className='result_header'>
                    <div className='result_title'>
                        <h2>Niðurstöður fyrir {`${data.name} (${insertDash(data.ssn)})`}</h2>
                        <FontAwesomeIcon onClick={() => cleanResultsAndForm()} icon={faXmark} className="icon-fa"/>
                    </div>
                    <div className='report-aside-component'>
                    </div>
                </div>

                <DisplayBox
                titleComponent={<>
                    Stjórnmálaleg tengsl: <span className={cx('answer',{'pos': data.connected})}>
                        {data.connected  ? 'Já' : 'Nei'}
                        </span>
                    </>
                }
                asideComponent={
                    <div className='link-wrapper'>
                        <FontAwesomeIcon icon={faFilePdf} className="icon-fa" />
                        <Link targetBlank linkSize='15' url={`/Pdf/Pep/${data.event.id}`}>Sækja skýrslu</Link>
                    </div>}>
                        <Table
                            tableSize='lg'
                            data={data.connected ? data.entries  : []}
                            columns={[
                                {
                                title: 'Aðili',
                                dataAccessor: ({ name }) => name,
                                textAlign: 'left',
                                },
                                {
                                title: 'Tengsl',
                                dataAccessor: ({ connection }) => connection,
                                textAlign: 'left',
                                },
                                {
                                title: 'Staða',
                                dataAccessor: ({ status }) => status,
                                textAlign: 'left',
                                },
                                {
                                title: 'Heimild',
                                dataAccessor: ({ source }) => source,
                                },
                            ]}
                        />
                        {
                            !data.connected && <p className='noResultText'>Engin stjórnmálaleg tengsl</p>
                        }
                </DisplayBox>
            </SectionWrapper>
        );
    }

    return (
        <div className='main KCL_pep-search'>
            <div className='main__inner-fluid'>
                <div className="shell">
                    <HeadSection
                        noSpaceBetween={!isAuthenticated}
                        title={
                            <>
                                <h1>PEP listi - stjórnmálaleg tengsl</h1>
                                {(isAuthenticated) && <HistoryButtonDropDown />}
                            </>
                        }
                        description={
                            <>
                                <p className='pep_headsection_summary'>Hér geta tilkynningarskyldir aðilar flett upp einstaklingum eftir kennitölu.</p>
                                <p className='pep_headsection_paragraph'>
                                    Á listanum eru einstaklingar sem eru eða hafa verið háttsettir í opinberri þjónustu ásamt nánustu fölskyldu
                                    og samstarfsmönnum á síðustu 18 mánuðum.&nbsp;
                                    <Link linkSize='18' url='/Skilmalar#peplisti'>Sjá skilmála.</Link>
                                </p>
                            </>
                        }
                    />
                    <div>{
                    (!isDisabled)
                    ? displayForm(false)
                    : <Tooltip
                        tooltip={
                            <span>
                                Eingöngu fyrir tilkynningarskylda aðila í áskrift.
                            </span>
                        }
                        text={displayForm(true)}
                        />
                    }</div>

                    {
                        isDisabled && <div style={{marginTop: '30px'}}>
                             <span className='disabled_text'>
                                 Ertu tilkynningarskyldur aðili en ekki með aðgang? <Link url="/Min-Kelda/Keyptar-Skyrslur/PEP" linkSize='18'>Hafðu samband.</Link>
                             </span>
                         </div>
                    }
                    {
                        isAuthenticated && <div style={{marginTop: '50px'}}>
                             <Link url="/Min-Kelda/Keyptar-Skyrslur/PEP" linkSize='16'
                                >
                                <FontAwesomeIcon icon={faAngleRight} className="icon-fa" />
                                Sjá allar uppflettingar
                            </Link>
                         </div>
                    }
                </div>
                {displayResultSection()}
            </div>
            <ModalsAndToasts />
        </div>
    );
}
const SectionWrapper = forwardRef<HTMLDivElement, {children?: ReactNode}>((props, ref) => {
    return <div className='section section--gray'>
        <div className='shell'>
            <div ref={ref as MutableRefObject<HTMLDivElement | null>} className='results'>{props.children}</div>
        </div>
    </div>
})

export default PEPSearch;

interface ISubFormValues {
    ssn: string
}

interface ISubFormProps {
    values: ISubFormValues
    loading: boolean
    disabled: boolean,
    buttonString: string,
}

const FormSubComponent: React.FC<ISubFormProps> = ({
    values,
    loading,
    disabled,
    buttonString,
}) => (
    <Form>
        <div className="form__body">
            <div className="form__section">
                <div className="pepSearchInputWrapper">
                    <div className="searchInput">
                        <Input
                            disabled={disabled}
                            label='Kennitala'
                            name="ssn"
                            id="ssn"
                            value={values.ssn}
                        />
                    </div>
                    <div className="searchButton">
                        <Button
                            buttonType={buttonString === "Kaupa" ? 'buy' : 'primary'}
                            showLoader={loading}
                            disabled={disabled}
                            size="lg"
                        >
                        {
                            buttonString
                        }
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </Form>
)